import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const adminApi = {
  // Upload Image
  uploadImage: async (file, authHeader) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/admin/upload`, formData, { 
      headers: { 
        ...authHeader,
        'Content-Type': 'multipart/form-data'
      } 
    });
    return response.data;
  },

  // Upload File (PDF, ZIP for digital downloads)
  uploadFile: async (file, authHeader) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/admin/upload-file`, formData, { 
      headers: { 
        ...authHeader,
        'Content-Type': 'multipart/form-data'
      } 
    });
    return response.data;
  },

  // Stats
  getStats: async (authHeader) => {
    const response = await axios.get(`${API}/admin/stats`, { headers: authHeader });
    return response.data;
  },

  // Reviews
  getAllReviews: async (authHeader, status = null, productId = null) => {
    let url = `${API}/admin/reviews`;
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (productId) params.append('product_id', productId);
    if (params.toString()) url += `?${params.toString()}`;
    const response = await axios.get(url, { headers: authHeader });
    return response.data;
  },

  getReviewStats: async (authHeader) => {
    const response = await axios.get(`${API}/admin/reviews/stats`, { headers: authHeader });
    return response.data;
  },

  createReview: async (reviewData, authHeader) => {
    const response = await axios.post(`${API}/admin/reviews`, reviewData, { headers: authHeader });
    return response.data;
  },

  updateReview: async (reviewId, reviewData, authHeader) => {
    const response = await axios.put(`${API}/admin/reviews/${reviewId}`, reviewData, { headers: authHeader });
    return response.data;
  },

  updateReviewStatus: async (reviewId, status, authHeader) => {
    const response = await axios.put(`${API}/admin/reviews/${reviewId}/status`, { status }, { headers: authHeader });
    return response.data;
  },

  deleteReview: async (reviewId, authHeader) => {
    const response = await axios.delete(`${API}/admin/reviews/${reviewId}`, { headers: authHeader });
    return response.data;
  },

  // Roles
  getAllRoles: async (authHeader) => {
    const response = await axios.get(`${API}/admin/roles`, { headers: authHeader });
    return response.data;
  },

  getUsersWithRoles: async (authHeader) => {
    const response = await axios.get(`${API}/admin/users/roles`, { headers: authHeader });
    return response.data;
  },

  createAdminUser: async (userData, authHeader) => {
    const response = await axios.post(`${API}/admin/users`, userData, { headers: authHeader });
    return response.data;
  },

  updateUserRole: async (userId, role, authHeader) => {
    const response = await axios.put(`${API}/admin/users/${userId}/role`, { role }, { headers: authHeader });
    return response.data;
  },

  toggleUserStatus: async (userId, isActive, authHeader) => {
    const response = await axios.put(`${API}/admin/users/${userId}/status`, { is_active: isActive }, { headers: authHeader });
    return response.data;
  },

  resetUserPassword: async (userId, newPassword, authHeader) => {
    const response = await axios.put(`${API}/admin/users/${userId}/reset-password`, { newPassword }, { headers: authHeader });
    return response.data;
  },

  // Products
  getAllProducts: async (authHeader) => {
    const response = await axios.get(`${API}/admin/products`, { headers: authHeader });
    return response.data;
  },

  createProduct: async (productData, authHeader) => {
    const response = await axios.post(`${API}/admin/products`, productData, { headers: authHeader });
    return response.data;
  },

  updateProduct: async (productId, productData, authHeader) => {
    const response = await axios.put(`${API}/admin/products/${productId}`, productData, { headers: authHeader });
    return response.data;
  },

  deleteProduct: async (productId, authHeader) => {
    const response = await axios.delete(`${API}/admin/products/${productId}`, { headers: authHeader });
    return response.data;
  },

  // Orders
  getAllOrders: async (authHeader) => {
    const response = await axios.get(`${API}/admin/orders`, { headers: authHeader });
    return response.data;
  },

  getOrderDetails: async (orderId, authHeader) => {
    const response = await axios.get(`${API}/admin/orders/${orderId}`, { headers: authHeader });
    return response.data;
  },

  updateOrderStatus: async (orderId, statusData, authHeader) => {
    const response = await axios.put(`${API}/admin/orders/${orderId}/status`, statusData, { headers: authHeader });
    return response.data;
  },

  // Scheduler Triggers
  triggerReviewRequests: async (authHeader) => {
    const response = await axios.post(`${API}/admin/scheduler/review-requests`, {}, { headers: authHeader });
    return response.data;
  },

  triggerLowStockCheck: async (authHeader) => {
    const response = await axios.post(`${API}/admin/scheduler/low-stock-check`, {}, { headers: authHeader });
    return response.data;
  },

  // Analytics Settings
  getAnalyticsSettings: async (authHeader) => {
    const response = await axios.get(`${API}/admin/analytics-settings`, { headers: authHeader });
    return response.data;
  },

  updateAnalyticsSettings: async (settings, authHeader) => {
    const response = await axios.put(`${API}/admin/analytics-settings`, settings, { headers: authHeader });
    return response.data;
  },

  // Password Change
  changePassword: async (passwordData, authHeader) => {
    const response = await axios.put(`${API}/admin/change-password`, passwordData, { headers: authHeader });
    return response.data;
  },

  // Settings
  getSettings: async (authHeader) => {
    const response = await axios.get(`${API}/admin/settings`, { headers: authHeader });
    return response.data;
  },

  updateSettings: async (settingsData, authHeader) => {
    const response = await axios.put(`${API}/admin/settings`, settingsData, { headers: authHeader });
    return response.data;
  },

  // Partners
  getAllPartners: async (authHeader) => {
    const response = await axios.get(`${API}/admin/partners`, { headers: authHeader });
    return response.data;
  },

  addPartner: async (partnerData, authHeader) => {
    const response = await axios.post(`${API}/admin/partners`, partnerData, { headers: authHeader });
    return response.data;
  },

  updatePartner: async (partnerId, partnerData, authHeader) => {
    const response = await axios.put(`${API}/admin/partners/${partnerId}`, partnerData, { headers: authHeader });
    return response.data;
  },

  deletePartner: async (partnerId, authHeader) => {
    const response = await axios.delete(`${API}/admin/partners/${partnerId}`, { headers: authHeader });
    return response.data;
  },

  // Categories
  getAllCategories: async (authHeader) => {
    const response = await axios.get(`${API}/admin/categories`, { headers: authHeader });
    return response.data;
  },

  createCategory: async (categoryData, authHeader) => {
    const response = await axios.post(`${API}/admin/categories`, categoryData, { headers: authHeader });
    return response.data;
  },

  updateCategory: async (slug, categoryData, authHeader) => {
    const response = await axios.put(`${API}/admin/categories/${slug}`, categoryData, { headers: authHeader });
    return response.data;
  },

  deleteCategory: async (slug, authHeader) => {
    const response = await axios.delete(`${API}/admin/categories/${slug}`, { headers: authHeader });
    return response.data;
  },

  // Categories (legacy - update banner only)
  updateCategoryBanner: async (slug, bannerData, authHeader) => {
    const response = await axios.put(`${API}/admin/categories/${slug}`, bannerData, { headers: authHeader });
    return response.data;
  },

  // Pages
  getAllPages: async (authHeader) => {
    const response = await axios.get(`${API}/admin/pages`, { headers: authHeader });
    return response.data;
  },

  createPage: async (pageData, authHeader) => {
    const response = await axios.post(`${API}/admin/pages`, pageData, { headers: authHeader });
    return response.data;
  },

  updatePage: async (pageId, pageData, authHeader) => {
    const response = await axios.put(`${API}/admin/pages/${pageId}`, pageData, { headers: authHeader });
    return response.data;
  },

  deletePage: async (pageId, authHeader) => {
    const response = await axios.delete(`${API}/admin/pages/${pageId}`, { headers: authHeader });
    return response.data;
  },

  // Users
  getAllUsers: async (authHeader) => {
    const response = await axios.get(`${API}/admin/users`, { headers: authHeader });
    return response.data;
  },

  createUser: async (userData, authHeader) => {
    const response = await axios.post(`${API}/admin/users`, userData, { headers: authHeader });
    return response.data;
  },

  updateUser: async (userId, userData, authHeader) => {
    const response = await axios.put(`${API}/admin/users/${userId}`, userData, { headers: authHeader });
    return response.data;
  },

  deleteUser: async (userId, authHeader) => {
    const response = await axios.delete(`${API}/admin/users/${userId}`, { headers: authHeader });
    return response.data;
  },

  // Newsletter
  getNewsletterSubscribers: async (authHeader) => {
    const response = await axios.get(`${API}/admin/newsletters`, { headers: authHeader });
    return response.data;
  },

  exportNewsletterSubscribers: async (authHeader) => {
    const response = await axios.post(`${API}/admin/newsletters/export`, {}, { headers: authHeader });
    return response.data;
  },

  // Discount Codes
  getDiscountCodes: async (authHeader) => {
    const response = await axios.get(`${API}/admin/discounts`, { headers: authHeader });
    return response.data;
  },

  createDiscountCode: async (discountData, authHeader) => {
    const response = await axios.post(`${API}/admin/discounts`, discountData, { headers: authHeader });
    return response.data;
  },

  updateDiscountCode: async (discountId, discountData, authHeader) => {
    const response = await axios.put(`${API}/admin/discounts/${discountId}`, discountData, { headers: authHeader });
    return response.data;
  },

  deleteDiscountCode: async (discountId, authHeader) => {
    const response = await axios.delete(`${API}/admin/discounts/${discountId}`, { headers: authHeader });
    return response.data;
  },

  // Activity Logs
  getActivityLogs: async (authHeader, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.user_filter) params.append('user_filter', filters.user_filter);
    if (filters.action_filter) params.append('action_filter', filters.action_filter);
    const response = await axios.get(`${API}/admin/activity-logs?${params.toString()}`, { headers: authHeader });
    return response.data;
  },

  getActivityActionTypes: async (authHeader) => {
    const response = await axios.get(`${API}/admin/activity-logs/actions`, { headers: authHeader });
    return response.data;
  },

  // Customers
  getCustomers: async (authHeader) => {
    const response = await axios.get(`${API}/admin/customers`, { headers: authHeader });
    return response.data;
  },

  // Clear Test Data
  clearTestData: async (authHeader) => {
    const response = await axios.post(`${API}/admin/clear-test-data`, {}, { headers: authHeader });
    return response.data;
  },

  // ============================================================
  // Enterprise Admin (Phase 2) — Invitations, Audit Log, Me
  // ============================================================
  getMe: async (authHeader) => {
    const response = await axios.get(`${API}/admin/me`, { headers: authHeader });
    return response.data;
  },

  getRoles: async (authHeader) => {
    const response = await axios.get(`${API}/admin/roles`, { headers: authHeader });
    return response.data;
  },

  // Invitations
  listInvitations: async (authHeader, status = null) => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    const response = await axios.get(`${API}/admin/invitations${params}`, { headers: authHeader });
    return response.data;
  },
  createInvitation: async (authHeader, { email, role }) => {
    const response = await axios.post(`${API}/admin/invitations`, { email, role }, { headers: authHeader });
    return response.data;
  },
  revokeInvitation: async (authHeader, invitationId) => {
    const response = await axios.post(`${API}/admin/invitations/${invitationId}/revoke`, {}, { headers: authHeader });
    return response.data;
  },
  resendInvitation: async (authHeader, invitationId) => {
    const response = await axios.post(`${API}/admin/invitations/${invitationId}/resend`, {}, { headers: authHeader });
    return response.data;
  },

  // Audit log
  getAuditLog: async (authHeader, { limit = 100, skip = 0, resourceType, action, actor } = {}) => {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (resourceType) params.set('resource_type', resourceType);
    if (action) params.set('action', action);
    if (actor) params.set('actor', actor);
    const response = await axios.get(`${API}/admin/audit-log?${params.toString()}`, { headers: authHeader });
    return response.data;
  },

  // ============================================================
  // Enterprise Admin (Phase 3) — Product content + SEO CMS
  // ============================================================
  updateProductContent: async (productId, payload, authHeader) => {
    const response = await axios.put(`${API}/admin/products/${productId}/content`, payload, { headers: authHeader });
    return response.data;
  },

  // ============================================================
  // Enterprise Admin (Phase 4) — Homepage CMS + Site Status
  // ============================================================
  getHomepageDraft: async (authHeader) => {
    const r = await axios.get(`${API}/admin/homepage/draft`, { headers: authHeader });
    return r.data;
  },
  saveHomepageDraft: async (authHeader, { sections, changeNotes = '' }) => {
    const r = await axios.put(`${API}/admin/homepage/draft`,
      { sections, change_notes: changeNotes }, { headers: authHeader });
    return r.data;
  },
  getHomepagePublished: async (authHeader) => {
    const r = await axios.get(`${API}/admin/homepage/published`, { headers: authHeader });
    return r.data;
  },
  publishHomepage: async (authHeader, { changeNotes = '', version = null } = {}) => {
    const body = { change_notes: changeNotes };
    if (version) body.version = version;
    const r = await axios.post(`${API}/admin/homepage/publish`, body, { headers: authHeader });
    return r.data;
  },
  getHomepageHistory: async (authHeader, { limit = 50, skip = 0 } = {}) => {
    const r = await axios.get(`${API}/admin/homepage/history?limit=${limit}&skip=${skip}`, { headers: authHeader });
    return r.data;
  },
  restoreHomepageVersion: async (authHeader, versionId, { changeNotes = '' } = {}) => {
    const r = await axios.post(`${API}/admin/homepage/history/${versionId}/restore`,
      { change_notes: changeNotes }, { headers: authHeader });
    return r.data;
  },
  getSiteStatus: async (authHeader) => {
    const r = await axios.get(`${API}/admin/site-status`, { headers: authHeader });
    return r.data;
  },
};

export default adminApi;